import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import MarketingLayout from "../components/marketing/MarketingLayout";
import { LineMask, FadeIn, SectionTag } from "../components/marketing/Reveal";

// EDIT HERE: placeholder clinical cases — replace with real anonymized cases anytime
const CASES = [
  {
    id: "Case 01", title: "Moderate Crowding + Deep Bite", tags: ["Crowding", "Deep bite", "Non-extraction"],
    summary: "Adult patient, 5mm lower anterior crowding with a 70% deep overbite. Resolved without extractions using arch development and controlled anterior intrusion.",
    diagnosis: "Class I molar relationship, moderate maxillary and mandibular anterior crowding, deep overbite (70%), retroclined upper incisors.",
    objectives: "Resolve crowding without extractions, open the bite to a physiological overlap, maintain molar relationship, level the curve of Spee.",
    stages: "22 stages upper / 24 stages lower", ipr: "1.8mm total, distributed 3-3 lower anterior",
    attachments: "Horizontal rectangular on premolars for anchorage; optimized-style bite ramps on upper incisors",
    decisions: "Anterior intrusion staged before final space closure to avoid round-tripping; expansion limited to 2mm per quadrant to stay within the periodontal envelope.",
  },
  {
    id: "Case 02", title: "Generalized Spacing", tags: ["Spacing", "Aesthetic"],
    summary: "Generalized maxillary spacing of 4mm with a midline diastema. Closed with controlled bodily movement and long-term retention planning.",
    diagnosis: "Class I with generalized maxillary spacing, 2mm midline diastema, mild tooth-size discrepancy.",
    objectives: "Close all spaces with esthetic distribution, maintain canine guidance, plan permanent retention against relapse.",
    stages: "14 stages upper / 10 stages lower", ipr: "None required",
    attachments: "Vertical rectangular on canines and premolars for root control during space closure",
    decisions: "Diastema closed with reciprocal bodily movement rather than tipping; residual tooth-size discrepancy resolved toward the posterior segments where invisible.",
  },
  {
    id: "Case 03", title: "Class II Division 1 Correction", tags: ["Class II", "Elastics", "Distalization"],
    summary: "End-on Class II with increased overjet of 6mm. Corrected with sequential distalization and Class II elastics — no surgery, no extractions.",
    diagnosis: "Class II division 1, 6mm overjet, mild upper crowding, proclined upper incisors, average growth pattern.",
    objectives: "Achieve Class I canine and molar relationship, reduce overjet to 2mm, resolve crowding within the distalized arch.",
    stages: "34 stages upper / 20 stages lower", ipr: "0.6mm upper anterior refinement only",
    attachments: "Distalization-support attachments on upper posteriors; precision cuts for Class II elastics canine-to-molar",
    decisions: "Sequential (V-pattern) distalization staged at 50% activation overlap; elastics prescribed full-time for the first 20 stages to support anchorage.",
  },
  {
    id: "Case 04", title: "Anterior Deep Bite", tags: ["Deep bite", "Intrusion"],
    summary: "100% anterior deep bite with palatal impingement. Opened with segmented intrusion mechanics and posterior anchorage preservation.",
    diagnosis: "Class I with 100% deep overbite, supra-erupted lower incisors, exaggerated curve of Spee, palatal soft-tissue impingement.",
    objectives: "Intrude lower incisors, level the curve of Spee, establish 20% physiological overbite, protect posterior anchorage.",
    stages: "26 stages lower / 16 stages upper", ipr: "0.4mm lower anterior",
    attachments: "Horizontal attachments premolar-to-premolar as intrusion anchorage units; bite ramps on upper incisors",
    decisions: "Lower incisor intrusion staged in two waves with a holding phase between them — reduces force decay and improves aligner tracking on the intrusion segment.",
  },
  {
    id: "Case 05", title: "Anterior Open Bite", tags: ["Open bite", "Extrusion"],
    summary: "3mm anterior open bite of dentoalveolar origin. Closed with relative posterior intrusion and guided anterior extrusion.",
    diagnosis: "Anterior open bite (-3mm), posterior dentoalveolar excess, adaptive tongue posture, Class I molars.",
    objectives: "Close the anterior open bite, establish incisal contact and canine guidance, avoid extruding anteriors beyond esthetic display limits.",
    stages: "28 stages upper / 28 stages lower", ipr: "None",
    attachments: "Extrusion attachments (horizontal bevelled) on upper and lower incisors; posterior attachments for intrusion anchorage",
    decisions: "Bite closure planned 60% from posterior intrusion, 40% anterior extrusion — protecting the smile line; habit control counselling flagged to the treating doctor as a stability factor.",
  },
  {
    id: "Case 06", title: "Lower Incisor Extraction Case", tags: ["Extraction", "Severe crowding"],
    summary: "9mm lower anterior crowding with thin symphysis. Planned around a single lower incisor extraction with controlled space closure.",
    diagnosis: "Class I with severe lower anterior crowding (9mm), thin mandibular symphysis, mild Bolton excess in the lower arch.",
    objectives: "Resolve crowding without violating the alveolar envelope, close the extraction space with root parallelism, finish to a functional Class I.",
    stages: "38 stages lower / 22 stages upper", ipr: "0.8mm upper anterior for Bolton balance",
    attachments: "Root-control attachments on teeth adjacent to the extraction site; anchorage attachments premolars and canines",
    decisions: "Single lower incisor extraction chosen over 4-premolar pattern to match the Bolton discrepancy; space closure staged slowly (0.15mm/stage) with over-correction of adjacent root tip.",
  },
];

function CaseCard({ c, i }) {
  const [open, setOpen] = useState(i === 0);
  return (
    <FadeIn delay={i * 0.05}>
      <div className={`rounded-2xl border transition-colors duration-500 ${open ? "border-neon/40 bg-white/[0.04]" : "border-white/10 bg-white/[0.02] hover:border-white/25"}`}>
        <button onClick={() => setOpen(!open)} data-testid={`case-card-toggle-${i}`}
          className="w-full flex items-start justify-between gap-6 text-left p-7 lg:p-8">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-neon">{c.id}</p>
            <h2 className="font-display font-bold text-2xl lg:text-3xl mt-2 text-white">{c.title}</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {c.tags.map((t) => (
                <span key={t} className="rounded-full border border-white/15 px-3 py-1 text-[10px] uppercase tracking-widest text-slate-400">{t}</span>
              ))}
            </div>
          </div>
          <ChevronDown className={`h-5 w-5 text-neon shrink-0 mt-2 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
        </button>
        {open && (
          <div className="px-7 lg:px-8 pb-8" data-testid={`case-card-detail-${i}`}>
            <p className="text-sm text-slate-300 leading-relaxed max-w-3xl">{c.summary}</p>
            <div className="mt-7 grid sm:grid-cols-2 gap-x-10 gap-y-6">
              {[["Diagnosis", c.diagnosis], ["Treatment objectives", c.objectives], ["Staging", c.stages], ["IPR", c.ipr], ["Attachments", c.attachments], ["Key biomechanical decisions", c.decisions]].map(([k, v]) => (
                <div key={k}>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">{k}</p>
                  <p className="text-sm text-slate-300 leading-relaxed">{v}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 flex items-center gap-3 rounded-xl border border-white/10 bg-ink-2/60 px-5 py-3">
              <span className="h-2 w-2 rounded-full bg-neon shrink-0" aria-hidden="true" />
              <p className="text-xs text-slate-500">Illustrative anonymized case summary. Every treatment plan is prescribed, reviewed and approved by the treating doctor.</p>
            </div>
          </div>
        )}
      </div>
    </FadeIn>
  );
}

export default function CaseLibrary() {
  return (
    <MarketingLayout>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
        <section className="pt-40 pb-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <SectionTag>Case Library</SectionTag>
            <h1 className="font-display font-black tracking-tight leading-[0.98] text-[clamp(2.6rem,6.5vw,5.5rem)] max-w-4xl">
              <LineMask delay={0.1}>Real planning decisions,</LineMask>
              <LineMask delay={0.25} className="text-neon">case by case.</LineMask>
            </h1>
            <FadeIn delay={0.5}>
              <p className="mt-8 max-w-2xl text-base md:text-lg text-slate-400 leading-relaxed">
                Treatment planning is not automated tooth movement. These anonymized cases show the diagnosis, staging, attachment and IPR reasoning behind OrthoPlaniX setups — the same clinical thinking applied to every case you submit.
              </p>
            </FadeIn>
          </div>
        </section>

        <section className="pb-24 lg:pb-32">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 space-y-5" data-testid="case-library-list">
            {CASES.map((c, i) => <CaseCard key={c.id} c={c} i={i} />)}
          </div>
        </section>

        <section className="pb-24 lg:pb-32">
          <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
            <FadeIn>
              <h2 className="font-display font-black tracking-tight text-3xl sm:text-4xl lg:text-5xl">Your next case could <span className="text-neon">plan like this.</span></h2>
            </FadeIn>
            <FadeIn delay={0.15}>
              <Link to="/register" data-testid="case-library-cta"
                className="group mt-10 inline-flex items-center gap-2 rounded-full bg-neon px-8 py-4 text-sm font-bold text-ink transition-transform duration-300 hover:scale-105 hover:shadow-[0_0_50px_rgba(0,229,255,0.5)]">
                Submit your first case <ArrowUpRight className="h-4 w-4" />
              </Link>
            </FadeIn>
          </div>
        </section>
      </motion.div>
    </MarketingLayout>
  );
}
